import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const fmtTs = (ts) => (ts ? new Date(ts * 1000).toLocaleString() : "—");

export default function ApiAuditLog() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({
    exchange: "",
    endpoint: "",
    ok: "",
  });

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const params = { limit: 100 };
      if (filters.exchange) params.exchange = filters.exchange;
      if (filters.endpoint) params.endpoint = filters.endpoint;
      if (filters.ok !== "") params.ok = filters.ok;
      const r = await api.get("/audit/exchange-calls", { params });
      setRows(Array.isArray(r.data?.rows) ? r.data.rows : []);
      setCount(r.data?.count || 0);
    } catch (err) {
      if (err?.response?.status !== 401) console.warn("audit log load failed", err?.message);
    } finally {
      setBusy(false);
    }
  }, [filters]);

  useEffect(() => { reload(); }, [reload]);

  const downloadCsv = () => {
    const qs = new URLSearchParams();
    if (filters.exchange) qs.set("exchange", filters.exchange);
    if (filters.endpoint) qs.set("endpoint", filters.endpoint);
    if (filters.ok !== "") qs.set("ok", filters.ok);
    qs.set("limit", "1000");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/audit/exchange-calls/export.csv?${qs.toString()}`;
    window.open(url, "_blank");
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `marqolix_api_audit_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mq-panel" data-testid="api-audit-log-panel">
      <div className="mq-panel-header">
        <div>
          <span className="mq-heading text-sm font-semibold tracking-wider">{t("apiLogTitle")}</span>
          <div className="text-[10px] text-[var(--mq-text-3)]">{t("apiLogSubtitle")} · {Math.min(5, rows.length)} / {count} rows</div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="api-log-refresh" className="mq-btn text-xs" onClick={reload} disabled={busy}>
            {t("apiLogRefresh")}
          </button>
          <button data-testid="api-log-export-csv" className="mq-btn text-xs" onClick={downloadCsv}>
            {t("apiLogExportCsv")}
          </button>
          <button data-testid="api-log-export-json" className="mq-btn text-xs" onClick={downloadJson}>
            {t("apiLogExportJson")}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 py-2 border-b grid grid-cols-1 sm:grid-cols-3 gap-2" style={{ borderColor: "var(--mq-border)" }}>
        <input
          data-testid="api-log-filter-exchange"
          className="mq-input text-xs"
          placeholder={t("apiLogFilterExchange")}
          value={filters.exchange}
          onChange={(e) => setFilters({ ...filters, exchange: e.target.value.trim() })}
        />
        <input
          data-testid="api-log-filter-endpoint"
          className="mq-input text-xs"
          placeholder={t("apiLogFilterEndpoint")}
          value={filters.endpoint}
          onChange={(e) => setFilters({ ...filters, endpoint: e.target.value })}
        />
        <select
          data-testid="api-log-filter-status"
          className="mq-input text-xs"
          value={filters.ok}
          onChange={(e) => setFilters({ ...filters, ok: e.target.value })}
        >
          <option value="">{t("apiLogFilterAll")}</option>
          <option value="true">{t("apiLogFilterOk")}</option>
          <option value="false">{t("apiLogFilterFail")}</option>
        </select>
      </div>

      {/* Table */}
      <div className="mq-table-scroll" data-testid="api-log-scroll">
        <table className="w-full text-xs font-mono" data-testid="api-log-table">
          <thead>
            <tr className="text-left mq-label">
              <th className="px-2 py-1">{t("apiLogColTs")}</th>
              <th className="px-2 py-1">{t("apiLogColExchange")}</th>
              <th className="px-2 py-1">{t("apiLogColEndpoint")}</th>
              <th className="px-2 py-1">{t("apiLogColMethod")}</th>
              <th className="px-2 py-1">{t("apiLogColStatus")}</th>
              <th className="px-2 py-1 text-right">{t("apiLogColLatency")}</th>
              <th className="px-2 py-1">{t("apiLogColExchCode")}</th>
              <th className="px-2 py-1">{t("apiLogColExchMsg")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan="8" className="px-2 py-3 text-center text-[var(--mq-text-3)]">{t("apiLogEmpty")}</td></tr>
            ) : rows.slice(0, 5).map((r) => (
              <tr key={r._id || r.request_id} className="border-t" style={{ borderColor: "var(--mq-border)" }}
                  data-testid={`api-log-row-${r.request_id || r._id}`}>
                <td className="px-2 py-1 whitespace-nowrap text-[var(--mq-text-3)]">{fmtTs(r.ts)}</td>
                <td className="px-2 py-1 uppercase">{r.exchange}</td>
                <td className="px-2 py-1 truncate" style={{ maxWidth: 220 }} title={r.endpoint}>{r.endpoint}</td>
                <td className="px-2 py-1">{r.method}</td>
                <td className="px-2 py-1 font-bold" style={{ color: r.ok ? "var(--mq-green)" : "var(--mq-red)" }}>
                  {r.ok ? "OK" : "FAIL"}
                </td>
                <td className="px-2 py-1 text-right">{r.duration_ms != null ? `${Math.round(r.duration_ms)}ms` : "—"}</td>
                <td className="px-2 py-1">{r.exchange_code || "—"}</td>
                <td className="px-2 py-1 truncate" style={{ maxWidth: 280 }} title={r.exchange_message || r.error_message}>
                  {r.exchange_message || r.error_message || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
